import { ArrowRight } from "lucide-react";
import AnimateIn from "../ui/AnimateIn";
import GoogleSignInButton from "./GoogleSignInButton";
import SignInLegalNote from "./SignInLegalNote";
import BoardMockup from "./mockups/BoardMockup";

interface HeroSectionProps {
  onSignIn: () => void;
  isLoading: boolean;
}

export default function HeroSection({ onSignIn, isLoading }: HeroSectionProps) {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
      {/* Decorative gradient blobs — hidden on mobile to prevent horizontal overscroll */}
      <div
        className="hidden sm:block absolute top-20 -left-32 w-96 h-96 rounded-full opacity-20 dark:opacity-10 blur-3xl landing-float"
        style={{ backgroundColor: "var(--color-primary)" }}
      />
      <div
        className="hidden sm:block absolute top-40 -right-32 w-80 h-80 rounded-full opacity-15 dark:opacity-10 blur-3xl landing-float-delayed"
        style={{ backgroundColor: "var(--color-accent-violet)" }}
      />
      <div
        className="hidden sm:block absolute -bottom-20 left-1/3 w-72 h-72 rounded-full opacity-10 dark:opacity-5 blur-3xl landing-float-slow"
        style={{ backgroundColor: "var(--color-accent-emerald)" }}
      />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Text */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <AnimateIn>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-warm-text-primary mb-6 leading-[1.1] tracking-tight">
              Your tasks,
              <br />
              <span className="landing-gradient-text italic">under control</span>
            </h1>
          </AnimateIn>
          <AnimateIn delay={100}>
            <p className="text-lg md:text-xl text-warm-text-secondary leading-relaxed mb-10 max-w-2xl mx-auto">
              Speak a task, put it on Google Calendar, talk to your agent, and let
              unfinished work follow you to the next day. JuDI keeps it under control.
            </p>
          </AnimateIn>
          <AnimateIn delay={200}>
            <div className="flex flex-col items-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <GoogleSignInButton
                  onClick={onSignIn}
                  isLoading={isLoading}
                  size="large"
                />
                <a
                  href="#features"
                  className="inline-flex items-center gap-2 text-warm-text-secondary hover:text-warm-blue transition-colors text-sm font-medium"
                >
                  See features
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <SignInLegalNote className="mt-4 max-w-sm" />
            </div>
          </AnimateIn>
        </div>

        {/* Hero mockup */}
        <AnimateIn delay={400} className="max-w-4xl mx-auto">
          <div className="landing-float-slow">
            <BoardMockup />
          </div>
        </AnimateIn>
      </div>
    </section>
  );
}
